import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { DatePicker } from '@/components/Datepicker'
import { ValidationCue } from '@/components/ValidationCue'
import type { SeededSpan } from '../hooks/useSeededSpan'

/**
 * The two ends of a span of days the candidates are drawn from.
 *
 * Neither end can be left behind the other, which used to be possible and said
 * nothing: a backwards span asks the index for nothing and creates nothing -
 * see `validRange` - so the memo quietly stopped filling. The last day is fenced
 * off from the days before the first, and a first day moved past the last takes
 * it along - see `setDate`. The fence goes on this end only: a span moving
 * backwards is moved by its first day, which has nothing ahead of it to be
 * fenced by.
 */
export const MemoDateFields = ({ span, today, invalid = false }: {
  span: Pick<SeededSpan, 'date' | 'lastDate' | 'setDate' | 'setToDate'>
  /** What each picker's "today" button sets, in the registry's timezone. */
  today: string
  /** Marks the span as what keeps the memo from being created. */
  invalid?: boolean
}): JSX.Element => {
  const { t } = useTranslation()

  return (
    <ValidationCue show={invalid} className='flex flex-wrap items-center gap-2'>
      <DatePicker
        date={new Date(`${span.date}T00:00:00`)}
        setDate={span.setDate}
        resetToday={() => span.setDate(today)}
        label={t('memo:labels.dateFrom')}
        forceYear
      />

      {/*
        The last day the span covers rather than the field's own value, which is
        empty until the template has seeded it - and an empty day is no date to
        show.

        Fenced off from the days before the first, which is a whole day
        comparison, so the first day itself stays pickable and the timezone the
        browser happens to be set to cannot move the boundary.
      */}
      <DatePicker
        date={new Date(`${span.lastDate}T00:00:00`)}
        setDate={span.setToDate}
        resetToday={() => span.setToDate(today)}
        disabled={{ before: new Date(`${span.date}T00:00:00`) }}
        label={t('memo:labels.dateTo')}
        forceYear
      />
    </ValidationCue>
  )
}
